import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import {
  addEdge,
  Background,
  BackgroundVariant,
  Connection,
  ConnectionMode,
  Controls,
  Edge,
  MarkerType,
  Node,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import ThesisToolbox from './ThesisToolbox';
import { CompanyNode, CompanyNodeData, ThemeNode, ThemeNodeData } from './ThesisCustomNodes';
import { companyService } from '../CompaniesPage/service/company.service';
import { Company, NewsItem } from '../../../types';
import { thesisService, TeseDetailDto, TeseSummaryDto, GoogleNewsPaisDto } from '../../../services/thesisService';
import { newsService } from '../../../services/newsService';

type ThesisNodeType = 'companyNode' | 'themeNode';
type ThesisNodeData = CompanyNodeData | ThemeNodeData;
type Sentiment = 'positive' | 'negative' | 'neutral';
type ThemeSentiment = 'Positivo' | 'Negativo' | 'Sem sentimento';

type ThesisEdgeData = {
  sentiment: Sentiment;
  relation: string;
};

type ThesisEditorProps = {
  companies: Company[];
  initialThesis: TeseDetailDto | null;
  googleNewsPaises: GoogleNewsPaisDto[];
  onBack: () => void;
  onSaved: (detail: TeseDetailDto) => void;
};

const buildLocalidadeKey = (gl?: string | null, hl?: string | null, ceid?: string | null): string => {
  return `${(gl || '').toLowerCase()}|${(hl || '').toLowerCase()}|${(ceid || '').toLowerCase()}`;
};

const findPaisByCodes = (
  paises: GoogleNewsPaisDto[],
  gl?: string | null,
  hl?: string | null,
  ceid?: string | null,
): GoogleNewsPaisDto | undefined => {
  if (!gl && !hl && !ceid) return undefined;
  const target = buildLocalidadeKey(gl, hl, ceid);
  return paises.find((pais) => buildLocalidadeKey(pais.gl, pais.hl, pais.ceid) === target);
};

const DEFAULT_EDGE_COLOR: Record<Sentiment, string> = {
  positive: '#16a34a',
  negative: '#dc2626',
  neutral: '#6b7280',
};

const buildEdgePresentation = (sentiment: Sentiment, relation: string) => ({
  label:
    relation ||
    (sentiment === 'positive'
      ? 'Impacto Positivo'
      : sentiment === 'negative'
        ? 'Impacto Negativo'
        : 'Sem sentimento'),
  style: { stroke: DEFAULT_EDGE_COLOR[sentiment], strokeWidth: 2.2 },
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: DEFAULT_EDGE_COLOR[sentiment],
  },
});

const nodeTypes = {
  companyNode: CompanyNode,
  themeNode: ThemeNode,
};

const normalizeThemeSentiment = (value?: string | null): ThemeSentiment => {
  if (!value || !value.trim()) return 'Sem sentimento';
  const v = value.trim().toLowerCase();
  if (v.includes('negativo')) return 'Negativo';
  if (v.includes('positivo')) return 'Positivo';
  if (v.includes('neutro') || v.includes('neutral') || v.includes('sem sentimento')) return 'Sem sentimento';
  return 'Sem sentimento';
};

/** IDs gerados só no cliente ao arrastar um novo tema (não enviar como id persistido no save). */
const isProvisionalThemeNodeId = (id: string) => /^theme-\d+-\d+$/.test(id);

const sentimentToEdge = (sentiment: ThemeSentiment): Sentiment =>
  sentiment === 'Negativo' ? 'negative' : sentiment === 'Positivo' ? 'positive' : 'neutral';

const CHART_RANGE_OPTIONS = ['1d', '5d', '1mo', '3mo', '6mo', '1y'] as const;
type ChartRangeOption = (typeof CHART_RANGE_OPTIONS)[number];

const parseChartDate = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  const normalized = numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
  const parsedNumeric = new Date(normalized);
  return Number.isNaN(parsedNumeric.getTime()) ? null : parsedNumeric;
};

const formatChartAxis = (date: Date, range: ChartRangeOption): string => {
  if (range === '1d') {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const getChartRangeBounds = (range: ChartRangeOption): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date(to);

  switch (range) {
    case '1d':
      from.setDate(to.getDate() - 1);
      break;
    case '5d':
      from.setDate(to.getDate() - 5);
      break;
    case '1mo':
      from.setMonth(to.getMonth() - 1);
      break;
    case '3mo':
      from.setMonth(to.getMonth() - 3);
      break;
    case '6mo':
      from.setMonth(to.getMonth() - 6);
      break;
    case '1y':
      from.setFullYear(to.getFullYear() - 1);
      break;
    default:
      from.setMonth(to.getMonth() - 1);
  }

  return { from, to };
};

const formatPrice = (value: number, currency = 'BRL'): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value || 0);

const getImpactPointColor = (impact?: NewsItem['impact']): string => {
  if (impact === 'positive') return '#16a34a';
  if (impact === 'negative') return '#dc2626';
  return '#6b7280';
};

type ComboboxOption = { value: string; label: string };

type ComboboxProps = {
  options: ComboboxOption[];
  selectedValue: string | null;
  onSelect: (value: string | null) => void;
  defaultLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
};

const Combobox: React.FC<ComboboxProps> = ({
  options,
  selectedValue,
  onSelect,
  defaultLabel,
  searchPlaceholder,
  emptyLabel,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue) || null,
    [options, selectedValue],
  );

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => option.label.toLowerCase().includes(term));
  }, [options, search]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as globalThis.Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const displayLabel = selectedOption?.label || defaultLabel;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>{displayLabel}</span>
        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1 text-sm">
            <li>
              <button
                type="button"
                onClick={() => {
                  onSelect(null);
                  setIsOpen(false);
                  setSearch('');
                }}
                className={`w-full px-3 py-2 text-left hover:bg-blue-50 ${!selectedOption ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'}`}
              >
                {defaultLabel}
              </button>
            </li>
            {filteredOptions.map((option) => {
              const isSelected = option.value === selectedValue;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(option.value);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={`w-full px-3 py-2 text-left hover:bg-blue-50 ${isSelected ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'}`}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
            {filteredOptions.length === 0 && (
              <li className="px-3 py-2 text-gray-500">{emptyLabel}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const getLanguageDisplayName = (code: string): string => {
  if (!code) return code;
  try {
    const displayNames = new Intl.DisplayNames(['pt-BR'], { type: 'language' });
    const name = displayNames.of(code);
    if (!name) return code;
    return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    return code;
  }
};

const buildCeid = (gl: string, hl: string): string => `${gl.toUpperCase()}:${hl}`;

const ThesisEditorCanvas: React.FC<ThesisEditorProps> = ({ companies, initialThesis, googleNewsPaises, onBack, onSaved }) => {
  const navigate = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<ThesisNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<ThesisEdgeData>>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [themeTermsDraft, setThemeTermsDraft] = useState('');
  const [thesisName, setThesisName] = useState('');
  const [anchorTicker, setAnchorTicker] = useState('');
  const [anchorSearchTerm, setAnchorSearchTerm] = useState('');
  const [isAnchorDropdownOpen, setIsAnchorDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedChartRange, setSelectedChartRange] = useState<ChartRangeOption>('1mo');
  const [isThesisInsightsLoading, setIsThesisInsightsLoading] = useState(false);
  const [thesisNewsItems, setThesisNewsItems] = useState<NewsItem[]>([]);
  const [priceHistory, setPriceHistory] = useState<Array<{ data: string; fechamento: number }>>([]);
  const [priceCurrency, setPriceCurrency] = useState('BRL');

  const { screenToFlowPosition } = useReactFlow();
  const themeCounterRef = useRef(1);

  const companyByTicker = useMemo(() => {
    const map = new Map<string, Company>();
    companies.forEach((company) => {
      const tickers = [
        company.ticker,
        ...(Array.isArray(company.tickers) ? company.tickers : []),
      ]
        .filter((ticker): ticker is string => Boolean(ticker))
        .map((ticker) => ticker.toUpperCase());

      tickers.forEach((ticker) => map.set(ticker, company));
    });
    return map;
  }, [companies]);

  const companyOptionList = useMemo(() => {
    return companies
      .map((company) => {
        const ticker = (company.ticker || company.tickers?.[0] || '').trim().toUpperCase();
        if (!ticker) return null;
        return {
          id: company.id,
          ticker,
          nome: company.nome,
        };
      })
      .filter((item): item is { id: string; ticker: string; nome: string } => Boolean(item));
  }, [companies]);

  const filteredAnchorCompanies = useMemo(() => {
    const normalizedTerm = anchorSearchTerm.trim().toLowerCase();
    if (!normalizedTerm) {
      return companyOptionList.slice(0, 5);
    }
    return companyOptionList.filter(
      (item) =>
        item.ticker.toLowerCase().includes(normalizedTerm) ||
        item.nome.toLowerCase().includes(normalizedTerm)
    );
  }, [anchorSearchTerm, companyOptionList]);

  const updateAnchorNode = useCallback(
    (ticker: string) => {
      if (!ticker) return;
      const company = companyByTicker.get(ticker.toUpperCase());
      const anchorNodeId = 'anchor-company-node';
      setNodes((currentNodes) => {
        const nodeExists = currentNodes.some((node) => node.id === anchorNodeId);
        const companyNodeData: CompanyNodeData = {
          ticker: ticker.toUpperCase(),
          companyId: company?.id,
          companyName: company?.nome,
        };

        if (nodeExists) {
          return currentNodes.map((node) =>
            node.id === anchorNodeId ? { ...node, data: companyNodeData } : node
          );
        }

        const anchorNode: Node<CompanyNodeData> = {
          id: anchorNodeId,
          type: 'companyNode',
          position: { x: 120, y: 260 },
          data: companyNodeData,
        };
        return [anchorNode, ...currentNodes];
      });
    },
    [companyByTicker, setNodes]
  );

  useEffect(() => {
    if (initialThesis) {
      setThesisName(initialThesis.nome);
      setAnchorTicker(initialThesis.tickerEmpresa);
      const matchedCompany = companyByTicker.get(initialThesis.tickerEmpresa.toUpperCase());
      setAnchorSearchTerm(
        matchedCompany
          ? `${initialThesis.tickerEmpresa.toUpperCase()} - ${matchedCompany.nome}`
          : initialThesis.tickerEmpresa.toUpperCase()
      );
      updateAnchorNode(initialThesis.tickerEmpresa);

      const themeNodes: Node<ThemeNodeData>[] = initialThesis.temas.map((tema, index) => {
        const themeSentiment = normalizeThemeSentiment(tema.sentimento);
        const matchedPais = findPaisByCodes(googleNewsPaises, tema.gl, tema.hl, tema.ceid);
        return {
          id: tema.id || `theme-${Date.now()}-${index}`,
          type: 'themeNode',
          position: { x: tema.posX || 420, y: tema.posY || 180 + index * 110 },
          data: {
            theme: tema.nome,
            terms: tema.termosBusca || [],
            context: tema.contexto || '',
            sentiment: themeSentiment,
            gl: tema.gl || null,
            hl: tema.hl || null,
            ceid: tema.ceid || null,
            localidadeLabel: matchedPais?.country || (tema.ceid || null),
          },
        };
      });

      setNodes((currentNodes) => {
        const anchor = currentNodes.find((node) => node.id === 'anchor-company-node');
        return anchor ? [anchor, ...themeNodes] : themeNodes;
      });

      const anchorId = 'anchor-company-node';
      const themeEdges: Edge<ThesisEdgeData>[] = themeNodes.map((node) => {
        const edgeSentiment = sentimentToEdge((node.data as ThemeNodeData).sentiment);
        return {
          id: `edge-${node.id}-${anchorId}`,
          source: node.id,
          target: anchorId,
          type: 'smoothstep',
          ...buildEdgePresentation(edgeSentiment, ''),
          data: {
            sentiment: edgeSentiment,
            relation: '',
          },
        };
      });
      setEdges(themeEdges);
      return;
    }

    setNodes([]);
    setEdges([]);
    setThesisName('');
    setAnchorTicker('');
    setAnchorSearchTerm('');
  }, [companyByTicker, googleNewsPaises, initialThesis, setEdges, setNodes, updateAnchorNode]);

  const persistedThemeIds = useMemo(
    () =>
      nodes
        .filter((node) => node.type === 'themeNode' && !isProvisionalThemeNodeId(node.id))
        .map((node) => node.id),
    [nodes]
  );

  const persistedThemeIdsSignature = useMemo(
    () => [...persistedThemeIds].sort().join('|'),
    [persistedThemeIds]
  );

  useEffect(() => {
    let isCurrent = true;

    const loadThesisInsights = async () => {
      if (!anchorTicker || !persistedThemeIdsSignature) {
        if (!isCurrent) return;
        setThesisNewsItems([]);
        setPriceHistory([]);
        return;
      }

      setIsThesisInsightsLoading(true);
      try {
        const [priceData, themeNewsResults] = await Promise.all([
          companyService.getTickerPriceData(anchorTicker, selectedChartRange),
          Promise.all(
            persistedThemeIdsSignature
              .split('|')
              .filter(Boolean)
              .map((temaId) => newsService.getNewsByTheme(temaId))
          ),
        ]);

        if (!isCurrent) return;

        const normalizedHistory = (priceData?.historicalDataPrice || [])
          .filter((point) => typeof point.fechamento === 'number')
          .map((point) => ({ data: point.data, fechamento: point.fechamento }))
          .sort((a, b) => {
            const aTime = parseChartDate(a.data)?.getTime() || 0;
            const bTime = parseChartDate(b.data)?.getTime() || 0;
            return aTime - bTime;
          });
        setPriceHistory(normalizedHistory);
        setPriceCurrency(priceData?.moeda || 'BRL');

        const mergedNews = themeNewsResults.flatMap((result) => result.items);
        const dedupedNews = Array.from(
          new Map(
            mergedNews.map((item) => [
              `${item.companyId || item.company}-${item.time}-${item.summary}`,
              item,
            ])
          ).values()
        ).sort((a, b) => {
          const aTime = parseChartDate(a.time)?.getTime() || 0;
          const bTime = parseChartDate(b.time)?.getTime() || 0;
          return bTime - aTime;
        });
        setThesisNewsItems(dedupedNews);
      } finally {
        if (isCurrent) {
          setIsThesisInsightsLoading(false);
        }
      }
    };

    loadThesisInsights();

    return () => {
      isCurrent = false;
    };
  }, [anchorTicker, persistedThemeIdsSignature, selectedChartRange]);

  const filteredThesisNewsItems = useMemo(() => {
    const { from, to } = getChartRangeBounds(selectedChartRange);
    const fromTime = from.getTime();
    const toTime = to.getTime();

    return thesisNewsItems.filter((item) => {
      const referenceDate = parseChartDate(item.analysisNews?.[0]?.time || item.time);
      if (!referenceDate) return false;
      const ts = referenceDate.getTime();
      return ts >= fromTime && ts <= toTime;
    });
  }, [selectedChartRange, thesisNewsItems]);

  const thesisPriceVsNewsOptions = useMemo(() => {
    const priceSeries = priceHistory
      .map((point) => {
        const date = parseChartDate(point.data);
        if (!date) return null;
        return {
          timestamp: date.getTime(),
          value: point.fechamento,
        };
      })
      .filter((point): point is { timestamp: number; value: number } => Boolean(point));

    const scatterData = filteredThesisNewsItems
      .map((item) => {
        const publicationDate = parseChartDate(item.analysisNews?.[0]?.time || item.time);
        if (!publicationDate || priceSeries.length === 0) return null;
        const newsTs = publicationDate.getTime();

        let nearest = priceSeries[0];
        let nearestDiff = Math.abs(priceSeries[0].timestamp - newsTs);
        for (let i = 1; i < priceSeries.length; i += 1) {
          const diff = Math.abs(priceSeries[i].timestamp - newsTs);
          if (diff < nearestDiff) {
            nearest = priceSeries[i];
            nearestDiff = diff;
          }
        }

        return {
          value: [newsTs, nearest.value],
          item,
          itemStyle: {
            color: getImpactPointColor(item.impact),
          },
        };
      })
      .filter(Boolean);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        formatter: (params: any) => {
          const rows = Array.isArray(params) ? params : [params];
          const dateValue = rows?.[0]?.value?.[0];
          const date = typeof dateValue === 'number' ? new Date(dateValue) : null;
          const header = date
            ? `<strong>${date.toLocaleString('pt-BR')}</strong>`
            : '<strong>Sem data</strong>';

          const body = rows
            .map((row: any) => {
              if (row.seriesName === 'Preço') {
                return `Preço: ${formatPrice(Number(row.value?.[1] || 0), priceCurrency)}`;
              }
              if (row.seriesName === 'Notícias da tese') {
                const dataItem = row.data?.item as NewsItem | undefined;
                return `Notícia: ${dataItem?.title || dataItem?.fact || 'Sem título'}`;
              }
              return '';
            })
            .filter(Boolean)
            .join('<br/>');

          return `${header}<br/>${body}`;
        },
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          formatter: (value: number) => formatChartAxis(new Date(value), selectedChartRange),
        },
      },
      yAxis: {
        type: 'value',
        scale: true,
        axisLabel: {
          formatter: (value: number) =>
            new Intl.NumberFormat('pt-BR', {
              maximumFractionDigits: 2,
            }).format(value),
        },
      },
      series: [
        {
          name: 'Preço',
          type: 'line',
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2.2, color: '#1d4ed8' },
          data: priceSeries.map((point) => [point.timestamp, point.value]),
        },
        {
          name: 'Notícias da tese',
          type: 'scatter',
          symbolSize: 10,
          data: scatterData,
        },
      ],
      grid: { left: 40, right: 20, top: 30, bottom: 40 },
    };
  }, [filteredThesisNewsItems, priceCurrency, priceHistory, selectedChartRange]);

  const thesisChartEvents = useMemo(
    () => ({
      click: (params: any) => {
        if (params?.seriesType !== 'scatter') return;
        const url = params?.data?.item?.analysisNews?.[0]?.url;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      },
    }),
    []
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const relation = 'Relaciona';
      const edgePresentation = buildEdgePresentation('neutral', relation);
      setEdges((currentEdges) => {
        const duplicate = currentEdges.some(
          (edge) => edge.source === connection.source && edge.target === connection.target
        );
        if (duplicate) return currentEdges;
        return addEdge(
          {
            ...connection,
            ...edgePresentation,
            id: `edge-${connection.source}-${connection.target}-${Date.now()}`,
            type: 'smoothstep',
            data: { sentiment: 'neutral', relation },
          } as Edge<ThesisEdgeData>,
          currentEdges
        );
      });
    },
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const droppedType = event.dataTransfer.getData('application/reactflow') as ThesisNodeType | '';
      if (droppedType !== 'themeNode') {
        return;
      }

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      const themeIndex = themeCounterRef.current;
      themeCounterRef.current += 1;

      const newNode: Node<ThemeNodeData> = {
        id: `theme-${Date.now()}-${themeIndex}`,
        type: 'themeNode',
        position,
        data: {
          theme: `Novo Tema ${themeIndex}`,
          terms: ['termo1', 'termo2'],
          context: '',
          sentiment: 'Sem sentimento',
          gl: null,
          hl: null,
          ceid: null,
          localidadeLabel: null,
        },
      };

      setNodes((currentNodes) => [...currentNodes, newNode]);
    },
    [screenToFlowPosition, setNodes]
  );

  const setEdgeSentiment = useCallback(
    (sentiment: Sentiment) => {
      if (!selectedEdgeId) return;
      setEdges((currentEdges) =>
        currentEdges.map((edge) => {
          if (edge.id !== selectedEdgeId) return edge;
          const relation = edge.data?.relation || '';
          return {
            ...edge,
            ...buildEdgePresentation(sentiment, relation),
            data: { sentiment, relation },
          };
        })
      );
    },
    [selectedEdgeId, setEdges]
  );

  const setEdgeRelation = useCallback(
    (relation: string) => {
      if (!selectedEdgeId) return;
      setEdges((currentEdges) =>
        currentEdges.map((edge) => {
          if (edge.id !== selectedEdgeId) return edge;
          const sentiment = edge.data?.sentiment || 'neutral';
          return {
            ...edge,
            ...buildEdgePresentation(sentiment, relation),
            data: { sentiment, relation },
          };
        })
      );
    },
    [selectedEdgeId, setEdges]
  );

  const selectedEdge = useMemo(
    () => edges.find((edge) => edge.id === selectedEdgeId) || null,
    [edges, selectedEdgeId]
  );
  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );

  const countryOptions = useMemo<ComboboxOption[]>(() => {
    const seen = new Set<string>();
    const options: ComboboxOption[] = [];
    googleNewsPaises.forEach((pais) => {
      const gl = (pais.gl || '').toLowerCase();
      if (!gl || seen.has(gl)) return;
      seen.add(gl);
      options.push({ value: gl, label: pais.country });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [googleNewsPaises]);

  const languageOptions = useMemo<ComboboxOption[]>(() => {
    const seen = new Set<string>();
    const options: ComboboxOption[] = [];
    googleNewsPaises.forEach((pais) => {
      const hl = pais.hl || '';
      if (!hl || seen.has(hl)) return;
      seen.add(hl);
      options.push({ value: hl, label: getLanguageDisplayName(hl) });
    });
    return options.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [googleNewsPaises]);

  const updateNodeData = useCallback(
    (nodeId: string, data: Partial<ThesisNodeData>) => {
      setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id !== nodeId) return node;
          return { ...node, data: { ...node.data, ...data } as ThesisNodeData };
        })
      );
    },
    [setNodes]
  );

  const saveThesis = async () => {
    const sanitizedName = thesisName.trim();
    const fromPicker = anchorTicker.trim().toUpperCase();
    const term = anchorSearchTerm.trim();
    const fromLabel =
      term.match(/^([A-Za-z0-9]+)\s*-\s+/)?.[1]?.toUpperCase() ||
      term.split(/\s+/)[0]?.replace(/[^A-Za-z0-9]/g, '')?.toUpperCase() ||
      '';
    const sanitizedTicker = fromPicker || fromLabel;

    if (!sanitizedName || !sanitizedTicker) {
      window.alert('Preencha o nome da tese e o ticker da empresa âncora.');
      return;
    }

    const themeNodes = nodes.filter((node) => node.type === 'themeNode');
    const temas = themeNodes.map((node) => {
      const data = node.data as ThemeNodeData;
      const base = {
        nome: data.theme.trim() || 'Tema sem nome',
        termosBusca: (data.terms || []).map((item) => item.trim()).filter(Boolean),
        posX: Number(node.position.x || 0),
        posY: Number(node.position.y || 0),
        sentimento:
          data.sentiment && data.sentiment !== 'Sem sentimento'
            ? data.sentiment
            : null,
        contexto: data.context?.trim() || null,
        gl: data.gl?.trim() || null,
        hl: data.hl?.trim() || null,
        ceid: data.ceid?.trim() || null,
      };
      return isProvisionalThemeNodeId(node.id) ? base : { ...base, id: node.id };
    });

    setIsSaving(true);
    try {
      const saved = await thesisService.save({
        ...(initialThesis?.id ? { id: initialThesis.id } : {}),
        nomeTese: sanitizedName,
        tickerEmpresa: sanitizedTicker,
        temas,
      });

      if (!saved) {
        window.alert('Não foi possível salvar a tese agora.');
        return;
      }

      onSaved(saved);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Nome da tese</label>
            <input
              type="text"
              value={thesisName}
              onChange={(event) => setThesisName(event.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex.: Tese Bancos 2026"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-gray-600">Empresa âncora (ticker)</label>
            <div className="relative">
              <input
                type="text"
                value={anchorSearchTerm}
                onFocus={() => setIsAnchorDropdownOpen(true)}
                onBlur={() => window.setTimeout(() => setIsAnchorDropdownOpen(false), 120)}
                onChange={(event) => {
                  const nextTerm = event.target.value;
                  setAnchorSearchTerm(nextTerm);
                  setAnchorTicker('');
                }}
                placeholder="Busque por ticker ou empresa"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {isAnchorDropdownOpen && (
                <div className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                  {filteredAnchorCompanies.length ? (
                    filteredAnchorCompanies.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onMouseDown={() => {
                          setAnchorTicker(item.ticker);
                          setAnchorSearchTerm(`${item.ticker} - ${item.nome}`);
                          updateAnchorNode(item.ticker);
                          setIsAnchorDropdownOpen(false);
                        }}
                        className="block w-full border-b border-gray-100 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 last:border-b-0"
                      >
                        <span className="font-semibold text-blue-900">{item.ticker}</span> - {item.nome}
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-2 text-sm text-gray-500">Nenhuma empresa encontrada.</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-end justify-end gap-2">
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Voltar
            </button>
            <button
              type="button"
              onClick={saveThesis}
              disabled={isSaving}
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {isSaving ? 'Salvando...' : 'Salvar Tese de Investimento'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
        <ThesisToolbox />
        <div className="relative h-[calc(100vh-280px)] w-full overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
          {selectedEdge && (
            <div className="absolute left-4 top-4 z-20 w-[280px] rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Conexão selecionada</p>
              <p className="mt-1 text-sm text-gray-700">{selectedEdge.source} -&gt; {selectedEdge.target}</p>
              <div className="mt-3">
                <label className="mb-1 block text-xs font-semibold text-gray-600">Relação</label>
                <input
                  type="text"
                  value={selectedEdge.data?.relation || ''}
                  onChange={(event) => setEdgeRelation(event.target.value)}
                  placeholder="Ex.: influencia, reduz demanda..."
                  className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEdgeSentiment('positive')}
                  className="rounded-lg bg-emerald-100 px-2 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-200"
                >
                  Positivo
                </button>
                <button
                  type="button"
                  onClick={() => setEdgeSentiment('negative')}
                  className="rounded-lg bg-red-100 px-2 py-2 text-xs font-semibold text-red-700 hover:bg-red-200"
                >
                  Negativo
                </button>
                <button
                  type="button"
                  onClick={() => setEdgeSentiment('neutral')}
                  className="col-span-2 rounded-lg bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                >
                  Sem sentimento
                </button>
              </div>
            </div>
          )}

          {selectedNode && selectedNode.type === 'themeNode' && (
            <div className="absolute left-4 top-[190px] z-20 w-[320px] rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tema selecionado</p>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Nome do tema</label>
                  <input
                    type="text"
                    value={(selectedNode.data as ThemeNodeData).theme}
                    onChange={(event) => updateNodeData(selectedNode.id, { theme: event.target.value })}
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Sentimento</label>
                  <select
                    value={(selectedNode.data as ThemeNodeData).sentiment || 'Sem sentimento'}
                    onChange={(event) => {
                      const nextSentiment = event.target.value as ThemeSentiment;
                      updateNodeData(selectedNode.id, { sentiment: nextSentiment });
                      const edgeSentiment = sentimentToEdge(nextSentiment);
                      setEdges((currentEdges) =>
                        currentEdges.map((edge) => {
                          if (edge.source !== selectedNode.id) return edge;
                          const relation = edge.data?.relation || '';
                          return {
                            ...edge,
                            ...buildEdgePresentation(edgeSentiment, relation),
                            data: { sentiment: edgeSentiment, relation },
                          };
                        })
                      );
                    }}
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Sem sentimento">Sem sentimento</option>
                    <option value="Positivo">Positivo</option>
                    <option value="Negativo">Negativo</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Localidade de busca</label>
                  <Combobox
                    options={countryOptions}
                    selectedValue={((selectedNode.data as ThemeNodeData).gl || '').toLowerCase() || null}
                    defaultLabel="Padrão (Brasil)"
                    searchPlaceholder="Pesquisar país..."
                    emptyLabel="Nenhum país encontrado."
                    onSelect={(nextGl) => {
                      const currentData = selectedNode.data as ThemeNodeData;
                      const currentHl = currentData.hl || null;
                      const gl = nextGl || null;
                      const countryLabel = gl
                        ? countryOptions.find((option) => option.value === gl)?.label || null
                        : null;

                      if (!gl && !currentHl) {
                        updateNodeData(selectedNode.id, {
                          gl: null,
                          hl: null,
                          ceid: null,
                          localidadeLabel: null,
                        });
                        return;
                      }

                      updateNodeData(selectedNode.id, {
                        gl,
                        hl: currentHl,
                        ceid: gl && currentHl ? buildCeid(gl, currentHl) : null,
                        localidadeLabel: countryLabel,
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Idioma de busca</label>
                  <Combobox
                    options={languageOptions}
                    selectedValue={(selectedNode.data as ThemeNodeData).hl || null}
                    defaultLabel="Padrão (Português)"
                    searchPlaceholder="Pesquisar idioma..."
                    emptyLabel="Nenhum idioma encontrado."
                    onSelect={(nextHl) => {
                      const currentData = selectedNode.data as ThemeNodeData;
                      const currentGl = (currentData.gl || '').toLowerCase() || null;
                      const hl = nextHl || null;

                      if (!currentGl && !hl) {
                        updateNodeData(selectedNode.id, {
                          gl: null,
                          hl: null,
                          ceid: null,
                          localidadeLabel: null,
                        });
                        return;
                      }

                      updateNodeData(selectedNode.id, {
                        gl: currentGl,
                        hl,
                        ceid: currentGl && hl ? buildCeid(currentGl, hl) : null,
                        localidadeLabel: currentData.localidadeLabel || null,
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Termos (vírgula)</label>
                  <input
                    type="text"
                    value={themeTermsDraft}
                    onChange={(event) => {
                      const nextTermsDraft = event.target.value;
                      setThemeTermsDraft(nextTermsDraft);
                      updateNodeData(selectedNode.id, {
                        terms: nextTermsDraft.split(',').map((term) => term.trim()).filter(Boolean),
                      });
                    }}
                    className="w-full rounded-lg border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-gray-600">Contexto para IA</label>
                  <textarea
                    value={(selectedNode.data as ThemeNodeData).context || ''}
                    onChange={(event) => updateNodeData(selectedNode.id, { context: event.target.value })}
                    rows={3}
                    className="w-full resize-none rounded-lg border border-gray-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            connectionMode={ConnectionMode.Loose}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onNodeClick={(_, node) => {
              setSelectedNodeId(node.id);
              setSelectedEdgeId(null);
              if (node.type === 'themeNode') {
                setThemeTermsDraft(((node.data as ThemeNodeData).terms || []).join(', '));
              } else {
                setThemeTermsDraft('');
              }
            }}
            onEdgeClick={(_, edge) => {
              setSelectedEdgeId(edge.id);
              setSelectedNodeId(null);
              setThemeTermsDraft('');
            }}
            onPaneClick={() => {
              setSelectedEdgeId(null);
              setSelectedNodeId(null);
              setThemeTermsDraft('');
            }}
            fitView
          >
            <Background variant={BackgroundVariant.Dots} gap={18} size={1.2} color="#cbd5e1" />
            <Controls />
          </ReactFlow>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Gráfico preço x notícias da tese</h2>
          <div className="flex flex-wrap gap-2">
            {CHART_RANGE_OPTIONS.map((range) => (
              <button
                key={range}
                type="button"
                onClick={() => setSelectedChartRange(range)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  selectedChartRange === range
                    ? 'border-blue-700 bg-blue-700 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>

        {isThesisInsightsLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-700"></div>
          </div>
        ) : priceHistory.length > 0 ? (
          <>
            <ReactECharts
              option={thesisPriceVsNewsOptions}
              style={{ height: 360, width: '100%' }}
              notMerge
              lazyUpdate
              onEvents={thesisChartEvents}
            />
            <p className="mt-2 text-xs text-gray-500">
              Pontos no gráfico representam notícias geradas pela tese. Clique em um ponto para abrir a notícia.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-600">
            Sem dados suficientes para o gráfico. Selecione empresa âncora e abra uma tese com temas já cadastrados.
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Notícias geradas pela tese</h2>
          <span className="text-sm text-gray-600">
            Total de análises: <strong>{filteredThesisNewsItems.length}</strong>
          </span>
        </div>

        {isThesisInsightsLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-700"></div>
          </div>
        ) : filteredThesisNewsItems.length === 0 ? (
          <p className="text-sm text-gray-600">
            Nenhuma notícia/análise encontrada para os temas da tese no período selecionado.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredThesisNewsItems.map((item) => {
              const analysisKey = item.analysisId || item.id;
              const handleOpen = () => {
                if (!analysisKey) return;
                navigate(`/dashboard/analise/${encodeURIComponent(analysisKey)}`);
              };
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  onClick={handleOpen}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpen();
                    }
                  }}
                  className={`cursor-pointer rounded-xl border p-4 transition-shadow hover:shadow-md ${
                    item.impact === 'positive'
                      ? 'border-green-200 bg-green-50'
                      : item.impact === 'negative'
                        ? 'border-red-200 bg-red-50'
                        : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900">{item.fact || item.title}</p>
                    <span className="text-xs text-gray-500">
                      {parseChartDate(item.analysisNews?.[0]?.time || item.time)?.toLocaleString('pt-BR') ||
                        item.analysisNews?.[0]?.time ||
                        item.time}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{item.summary}</p>
                  {item.analysisNews && item.analysisNews.length > 0 && (
                    <ul className="space-y-1">
                      {item.analysisNews.map((news, index) => (
                        <li key={`${news.url}-${index}`} className="text-sm">
                          <a
                            href={news.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-700 hover:text-blue-900 hover:underline"
                          >
                            {news.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const InvestmentThesisPage: React.FC = () => {
  const [filterNome, setFilterNome] = useState('');
  const [filterTicker, setFilterTicker] = useState('');
  const [theses, setTheses] = useState<TeseSummaryDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [usageTotalThemes, setUsageTotalThemes] = useState<number | null>(null);

  const [viewMode, setViewMode] = useState<'list' | 'editor'>('list');
  const [editingThesis, setEditingThesis] = useState<TeseDetailDto | null>(null);
  const [googleNewsPaises, setGoogleNewsPaises] = useState<GoogleNewsPaisDto[]>([]);

  const loadTheses = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await thesisService.list({
        nome: filterNome.trim() || undefined,
        ticker: filterTicker.trim() || undefined,
      });
      setTheses(result);
    } finally {
      setIsLoading(false);
    }
  }, [filterNome, filterTicker]);

  useEffect(() => {
    loadTheses();
  }, [loadTheses]);

  useEffect(() => {
    const bootstrap = async () => {
      const [companiesResult, usage, paises] = await Promise.all([
        companyService.getAllCompanies(),
        thesisService.getUsage(),
        thesisService.listGoogleNewsPaises(),
      ]);
      setCompanies(companiesResult);
      setUsageTotalThemes(usage?.totalTemas ?? null);
      setGoogleNewsPaises(paises);
    };
    bootstrap();
  }, []);

  const openCreate = () => {
    setEditingThesis(null);
    setViewMode('editor');
  };

  const openEdit = async (id: string) => {
    setIsLoading(true);
    try {
      const detail = await thesisService.getById(id);
      if (!detail) {
        window.alert('Não foi possível carregar a tese selecionada.');
        return;
      }
      setEditingThesis(detail);
      setViewMode('editor');
    } finally {
      setIsLoading(false);
    }
  };

  if (viewMode === 'editor') {
    return (
      <div className="mx-auto max-w-[1600px] px-4 py-6 md:px-6 md:py-8">
        <ThesisEditorCanvas
          companies={companies}
          initialThesis={editingThesis}
          googleNewsPaises={googleNewsPaises}
          onBack={() => setViewMode('list')}
          onSaved={async (detail) => {
            setEditingThesis(detail);
            await loadTheses();
          }}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">Teses de Investimento</h1>
          <p className="mt-1 text-sm text-gray-600">Filtre, visualize e edite teses já cadastradas antes de abrir o mapa mental.</p>
          {usageTotalThemes !== null && (
            <p className="mt-2 text-xs font-medium text-blue-700">Uso atual (Billing): {usageTotalThemes} nós de tema</p>
          )}
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800"
        >
          Nova tese
        </button>
      </div>

      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_220px_auto]">
          <input
            type="text"
            value={filterNome}
            onChange={(event) => setFilterNome(event.target.value)}
            placeholder="Filtrar por nome da tese"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            value={filterTicker}
            onChange={(event) => setFilterTicker(event.target.value.toUpperCase())}
            placeholder="Ticker (ex.: ITUB4)"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={loadTheses}
            className="rounded-lg border border-blue-700 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
          >
            Buscar
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-700"></div>
          </div>
        ) : theses.length === 0 ? (
          <div className="p-8 text-sm text-gray-500">Nenhuma tese encontrada com os filtros atuais.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {theses.map((thesis) => (
              <div key={thesis.id} className="flex flex-wrap items-center justify-between gap-3 p-4">
                <div>
                  <p className="font-semibold text-gray-900">{thesis.nome}</p>
                  <p className="text-sm text-gray-600">
                    Empresa âncora: <span className="font-medium">{thesis.tickerEmpresa}</span> • Temas: {thesis.totalTemas}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(thesis.id)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Abrir / Editar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const InvestmentThesisPageWrapper: React.FC = () => (
  <ReactFlowProvider>
    <InvestmentThesisPage />
  </ReactFlowProvider>
);

export default InvestmentThesisPageWrapper;
